import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDictOptions } from '@/redux/components/dictOptionsSlice';
import { fetchMonitoringStatistics } from '@/redux/alerts/monitoringStatisticsSlice';
import i2Math from '@/utils/i2Math';
import {
  connectSse,
  disconnectSse,
  SSE_CONSUMER_IDS
} from '@/redux/sse/sseSlice';
import { formatDateTimeStr } from '@/utils/renderUtils';
import { formatSurveillanceAlertDuration } from '@/views/surveillance/components/SurveillanceAlertOverlay';
import { babyMonitorDevices, readBabyMonitorView } from './BabyMonitor';

const PAGE_SIZE = 10;

function slotCount(raw) {
  const n = Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(n)) return null;
  return n;
}

function slotLabel(slot) {
  const hours = Number(slot && slot.hours);
  if (Number.isFinite(hours) && hours > 0) return `${hours}小时`;
  const name = slot && slot.slot != null ? String(slot.slot).trim() : '';
  return name || '--';
}

function alertLevel(view) {
  if (view.hasBaby === false) return { text: '提醒', color: '#fbbf24' };
  return { text: '紧急', color: '#ff6b6b' };
}

function MonitoringAlert({ storeId }) {
  const dispatch = useDispatch();
  const sse = useSelector((state) => state.sse);
  const yingshiDeviceList = useSelector((state) => state.yingshi.deviceList);
  const dictOptions = useSelector((state) => state.components.dictOptions);
  const monitoringStatistics = useSelector((state) => state.alerts.monitoringStatistics);
  const [openAlerts, setOpenAlerts] = useState(false);
  const [alertPage, setAlertPage] = useState(1);
  const [doneIndex, setDoneIndex] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';

  useEffect(() => {
    dispatch(connectSse({ consumerId: SSE_CONSUMER_IDS.VISUAL_SCREEN }));
    return () => {
      dispatch(disconnectSse({ consumerId: SSE_CONSUMER_IDS.VISUAL_SCREEN }));
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDictOptions({ typeCode: 'nursing_type' }));
  }, [dispatch]);

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    dispatch(fetchMonitoringStatistics({ query: { storeId: selectedStoreId } }))
      .unwrap()
      .catch(() => {});
    return undefined;
  }, [dispatch, selectedStoreId]);

  useEffect(() => {
    if (!openAlerts) return undefined;
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [openAlerts]);

  const nursingLabelByValue = useMemo(() => {
    const list = Array.isArray(dictOptions?.nursing_type?.data)
      ? dictOptions.nursing_type.data
      : [];
    const map = new Map();
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      if (!item || item.value == null || item.value === '') continue;
      const raw = item.label != null ? item.label : (item.desc != null ? item.desc : item.value);
      map.set(String(item.value), String(raw == null ? '' : raw).trim());
    }
    return map;
  }, [dictOptions]);

  const alerts = useMemo(() => {
    const devices = babyMonitorDevices(yingshiDeviceList, selectedStoreId);
    return devices
      .map((device) => readBabyMonitorView(device, sse, nursingLabelByValue))
      .filter((view) => view.warning);
  }, [yingshiDeviceList, selectedStoreId, sse, nursingLabelByValue]);

  const count = alerts.length;
  const alertPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const alertSafePage = Math.min(alertPage, alertPages);
  const alertRows = alerts.slice((alertSafePage - 1) * PAGE_SIZE, alertSafePage * PAGE_SIZE);
  const statsSameStore = String(monitoringStatistics?.query?.storeId ?? '') === selectedStoreId;
  const rawSlots = statsSameStore && Array.isArray(monitoringStatistics?.data?.slots)
    ? monitoringStatistics.data.slots
    : [];
  const doneSlots = rawSlots.slice().sort((a, b) => (Number(a && a.hours) || 0) - (Number(b && b.hours) || 0));
  const doneCfg = doneIndex != null ? doneSlots[doneIndex] : null;
  const doneProcessed = doneCfg ? slotCount(doneCfg.processed) : null;

  return (
    <div className="monitoring-alert-card">
      <div className="ma-header">
        <div className="ma-title-row">
          <span className="ma-title">监控预警</span>
        </div>
      </div>
      <div className="ma-body">
        <div className="ma-count-wrapper">
          <div
            className={`ma-count${count === 0 ? ' muted' : ''}`}
            onClick={() => {
              if (count === 0) return;
              setAlertPage(1);
              setOpenAlerts(true);
            }}
            title={count === 0 ? '暂无未处理预警' : '点击查看未处理预警'}
          >
            <span className={`ma-count-num${count === 0 ? ' muted' : ''}`}>{count}</span>
            <span className="ma-count-unit">条</span>
          </div>
        </div>
        <div className="ma-done-panel">
          <div className="ma-done-title">已完成预警</div>
          <div className="ma-done-list">
            {doneSlots.map((slot, index) => {
              const processed = slotCount(slot.processed);
              const total = slotCount(slot.total);
              const pct = processed == null || !total
                ? null
                : Math.round(i2Math.i2Multiply(i2Math.i2Division(processed, total), 100));
              const label = slotLabel(slot);
              const processedText = processed == null ? '--' : String(processed);
              return (
                <div
                  key={slot.slot || slot.hours || index}
                  className="ma-done-item"
                  onClick={() => setDoneIndex(index)}
                  title={`${label}内已完成 ${processedText} 条`}
                >
                  <div className="ma-done-top">
                    <span className="ma-done-num">{processedText}</span>
                    <span className="ma-done-label">{label}</span>
                  </div>
                  <div className="ma-done-bar">
                    <div className="ma-done-fill" style={{ width: `${pct == null ? 0 : pct}%` }}></div>
                    <span className="ma-done-pct">{pct == null ? '--' : `${pct}%`}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {openAlerts && createPortal(
        <div className="ma-modal-overlay" onClick={() => setOpenAlerts(false)}>
          <div className="ma-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ma-modal-header">
              <div className="ma-modal-title-wrap">
                <span className="ma-modal-title ma-modal-title-alert">未处理预警</span>
                <span className="ma-modal-subtitle">{`共 ${count} 条`}</span>
              </div>
              <button type="button" className="ma-modal-close" onClick={() => setOpenAlerts(false)}>×</button>
            </div>
            <div className="ma-modal-thead">
              <span className="ma-th ma-th-bed">床位</span>
              <span className="ma-th ma-th-room">位置</span>
              <span className="ma-th ma-th-msg">预警条目</span>
              <span className="ma-th ma-th-level">等级</span>
              <span className="ma-th ma-th-start">发生时间</span>
              <span className="ma-th ma-th-handle-time">已持续</span>
            </div>
            <div className="ma-modal-tbody">
              {alertRows.map((view) => {
                const level = alertLevel(view);
                const durationMs = view.eventTimeMs > 0 ? Math.max(0, nowMs - view.eventTimeMs) : 0;
                return (
                  <div key={view.key} className="ma-tr">
                    <span className="ma-td ma-td-bed" style={{ color: level.color }}>{view.bedNo || '--'}</span>
                    <span className="ma-td ma-td-room">{view.room || '--'}</span>
                    <span className="ma-td ma-td-msg" title={view.warningText}>{view.warningText}</span>
                    <span className="ma-td ma-td-level">
                      <span className="ma-level-dot" style={{ background: level.color }}></span>
                      <span style={{ color: level.color }}>{level.text}</span>
                    </span>
                    <span className="ma-td ma-td-start">
                      {view.eventTimeMs > 0 ? formatDateTimeStr(view.eventTimeMs) : '--'}
                    </span>
                    <span className="ma-td ma-td-handle-time" style={{ color: level.color }}>
                      {durationMs > 0 ? formatSurveillanceAlertDuration(durationMs) : '--'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="ma-modal-pager">
              <button
                type="button"
                className="ma-page-btn"
                disabled={alertSafePage <= 1}
                onClick={() => setAlertPage((p) => Math.max(1, p - 1))}
              >
                ◀ 上一页
              </button>
              <span className="ma-page-info">
                第 <em>{alertSafePage}</em> / {alertPages} 页
              </span>
              <button
                type="button"
                className="ma-page-btn"
                disabled={alertSafePage >= alertPages}
                onClick={() => setAlertPage((p) => Math.min(alertPages, p + 1))}
              >
                下一页 ▶
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {doneCfg && createPortal(
        <div className="ma-modal-overlay" onClick={() => setDoneIndex(null)}>
          <div className="ma-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ma-modal-header">
              <div className="ma-modal-title-wrap">
                <span className="ma-modal-title">{`已完成预警 · ${slotLabel(doneCfg)}`}</span>
                <span className="ma-modal-subtitle">{`已完成 ${doneProcessed == null ? '--' : String(doneProcessed)} 条 · 明细待接口`}</span>
              </div>
              <button type="button" className="ma-modal-close" onClick={() => setDoneIndex(null)}>×</button>
            </div>
            <div className="ma-modal-thead">
              <span className="ma-th ma-th-bed">床位</span>
              <span className="ma-th ma-th-room">位置</span>
              <span className="ma-th ma-th-msg">预警条目</span>
              <span className="ma-th ma-th-level">等级</span>
              <span className="ma-th ma-th-start">开始时间</span>
              <span className="ma-th ma-th-end">结束时间</span>
              <span className="ma-th ma-th-handler">处理人</span>
              <span className="ma-th ma-th-handle-time">总消耗时间</span>
            </div>
            <div className="ma-modal-tbody">
              <div className="ma-tr ma-tr-empty">明细接口未接入</div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default MonitoringAlert;
